<template>
	<VerticalLayout>
		<Row :gutter="[16, 8]">
			<Col :span="18" :offset="0">
				<BaseStationSatellitesCountChart
					:apiData="baseStationApiData"
					:isLoading="baseStationIsLoading"
					:datesRangeSafe="datesRangeSafe"
				/>
			</Col>
			<Col :span="6" :offset="0">
				<Card :bordered="false">
					<VerticalLayout>
						{{ $t('stats.by-gnss.modeLabel') }}
						<Select
							class="width-100"
							v-model:value="aggFunction"
							:options="modeOptions"
							@change="fetchStatsItems"
						/>
						<GnssSelect ref="gnssSelectRef" :onChange="fetchStatsItems" />
						<DateRangeChooser v-model="datesRange" @change="fetchStatsItems" />
					</VerticalLayout>
				</Card>
			</Col>
		</Row>
		<Row v-if="allowPppGp" :gutter="[16, 8]">
			<Col :span="18" :offset="0">
				<GridpointSatellitesCountChart
					:apiData="gridpointApiData"
					:isLoading="gridpointIsLoading"
					:datesRangeSafe="datesRangeSafe"
				/>
			</Col>
			<Col :span="6" :offset="0">
				<Card v-if="multiCnetEnabled" :bordered="false">
					<CNetSelectStoreBound :onChange="fetchGridpointStatsItems" />
				</Card>
			</Col>
		</Row>
		<Row :gutter="[16, 8]">
			<Col :span="18" :offset="0">
				<SatellitesVisibilityCountChart
					:apiData="visibilityApiData"
					:isLoading="visibilityIsLoading"
					:datesRangeSafe="datesRangeSafe"
					:aggFunction="aggFunction"
				/>
			</Col>
		</Row>
	</VerticalLayout>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { Row, Col, Card, Select } from 'ant-design-vue';
import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';
import Consts from '@/api/Consts';

import { useSessionStore } from '@/stores/session';
import { useSubNetworksStore } from '@/stores/subNetworksStore';
import { useLicenseStore } from '@/stores/licenseStore';
import { useDateRangeAdapter } from '@/utils/useDateRangeAdapter';

import VerticalLayout from '@/components/common/VerticalLayout.vue';
import BaseStationSatellitesCountChart from '@/components/stats/BaseStationSatellitesCountChart.vue';
import GridpointSatellitesCountChart from '@/components/stats/GridpointSatellitesCountChart.vue';
import SatellitesVisibilityCountChart from '@/components/stats/SatellitesVisibilityCountChart.vue';
import GnssSelect from '@/components/common/controls/GnssSelect.vue';
import CNetSelectStoreBound from '@/components/common/controls/CNetSelectStoreBound.vue';
import DateRangeChooser from '@/components/common/DateRangeChooser.vue';


export default {
	components: {
		Row, Col, Card, Select,
		VerticalLayout, GnssSelect, DateRangeChooser, CNetSelectStoreBound,
		BaseStationSatellitesCountChart, GridpointSatellitesCountChart, SatellitesVisibilityCountChart,
	},
	setup() {
		const { t } = useI18n();
		const multiCnetEnabled = Consts.MULTI_CNET_ENABLED;
		const {
			apiData: baseStationApiData,
			isLoading: baseStationIsLoading,
			doRequest: doBaseStationRequest,
		} = Api.useSatellitesPerBaseStationCountAggregatedGet();
		const {
			apiData: gridpointApiData,
			isLoading: gridpointIsLoading,
			doRequest: doGridpointRequest,
		} = Api.useSatellitesPerGridpointCountAggregatedGet();
		const {
			apiData: visibilityApiData,
			isLoading: visibilityIsLoading,
			doRequest: doVisibilityRequest,
		} = Api.useSatellitesVisibilityAggregatedGet();
		const sessionStore = useSessionStore();
		const subNetworksStore = useSubNetworksStore();
		const licenseStore = useLicenseStore();
		const allowPppGp = computed(() => licenseStore.allowPppGp);
		const { datesRange, datesRangeStr, datesRangeSafe } = useDateRangeAdapter(sessionStore, 1, 0);
		const gnssSelectRef = ref();
		const aggFunction = ref(Consts.AGGREGATE_BS_SAT_SET_COUNT);

		const modeOptions = computed(() => [
			{
				value: Consts.AGGREGATE_BS_SAT_SET_COUNT,
				label: t('stats.by-gnss.modeBsSatellite'),
			},
			{
				value: Consts.AGGREGATE_BS_SET_COUNT,
				label: t('stats.by-gnss.modeBs'),
			},
		]);

		const getCnetNumberForRequest = () => {
			const subNetwork = subNetworksStore.subNetworkSelectedObject;
			if (subNetwork == null || subNetwork.cnets.length === 0) {
				return null;
			}
			if (multiCnetEnabled && subNetworksStore.cnetSelectedId != null) {
				const selectedCnet = subNetwork.cnets.find(
					item => item.id === subNetworksStore.cnetSelectedId,
				);
				if (selectedCnet != null) {
					return selectedCnet.cnet_number;
				}
			}
			return subNetwork.cnets[0].cnet_number;
		};

		const getCommonRequestData = () => {
			const gnssSelectedValue = gnssSelectRef.value != null ? gnssSelectRef.value.selected : [];
			return {
				start_date: datesRangeStr.value[0],
				end_date: datesRangeStr.value[1],
				agg_function: aggFunction.value,
				sub_network_id: subNetworksStore.subNetworkSelectedId,
				gnss: gnssSelectedValue,
			};
		};

		const fetchGridpointStatsItems = () => {
			if (!allowPppGp.value) {
				return;
			}
			if (datesRangeStr.value != null && subNetworksStore.subNetworkSelectedId != null) {
				doGridpointRequest({
					...getCommonRequestData(),
					cnet_number: getCnetNumberForRequest(),
				});
			}
		};

		const fetchStatsItems = () => {
			if (datesRangeStr.value != null && subNetworksStore.subNetworkSelectedId != null) {
				const requestData = getCommonRequestData();
				doBaseStationRequest(requestData);
				if (allowPppGp.value) {
					doGridpointRequest({
						...requestData,
						cnet_number: getCnetNumberForRequest(),
					});
				}
				doVisibilityRequest(requestData);
			}
		};

		watch(() => subNetworksStore.subNetworkSelectedId, () => {
			fetchStatsItems();
		});

		watch(() => subNetworksStore.cnetSelectedId, () => {
			if (multiCnetEnabled && allowPppGp.value) {
				fetchGridpointStatsItems();
			}
		});

		fetchStatsItems();

		return {
			multiCnetEnabled,
			allowPppGp,
			baseStationApiData,
			baseStationIsLoading,
			gridpointApiData,
			gridpointIsLoading,
			visibilityApiData,
			visibilityIsLoading,
			datesRange,
			datesRangeSafe,
			gnssSelectRef,
			aggFunction,
			modeOptions,
			fetchStatsItems,
			fetchGridpointStatsItems,
		};
	},
}
</script>
